import { DollarSign } from "lucide-react";
import {
  BooleanField,
  Create,
  Datagrid,
  DateField,
  DateInput,
  Edit,
  List,
  NumberField,
  NumberInput,
  ReferenceField,
  ReferenceInput,
  SelectInput,
  TabbedForm,
  FormTab,
  TextField,
  TextInput,
  TopToolbar,
  FilterButton,
  CreateButton,
  useNotify,
  useRedirect,
  useRefresh,
} from "react-admin";
import { EditorToolbar, SpeakerInput } from "../../components";
import { DownloadExportButton2 } from "../../components/list/DownloadExportButton";

function renderEditForm() {
  return (
    <TabbedForm toolbar={<EditorToolbar />}>
      <FormTab label="Details" replace>
        <SpeakerInput source="speaker_id" />
        <ReferenceInput
          source="ta_credit_period_id"
          reference="ta-credit-periods"
          label="Credit Period (Optional)"
        >
          <SelectInput optionText="name" />
        </ReferenceInput>
        <NumberInput source="credits" label="Credits (can be negative)" />
        <TextInput
          source="source"
          label="Source"
          multiline
          rows={3}
          helperText="Description of where credits came from or were spent"
        />
      </FormTab>
    </TabbedForm>
  );
}

function CreateView(props) {
  const notify = useNotify();
  const refresh = useRefresh();
  const redirect = useRedirect();
  return (
    <Create
      {...props}
      mutationMode="pessimistic"
      mutationOptions={{
        onSuccess: () => {
          notify("Credit transaction created successfully!", {
            type: "success",
          });
          refresh();
          redirect("list", "ta-credit-transactions");
        },
        onError: (error) => {
          notify(`Error: ${error.message}`, { type: "error" });
        },
      }}
    >
      {renderEditForm()}
    </Create>
  );
}

function EditView(props) {
  const notify = useNotify();
  const refresh = useRefresh();
  const redirect = useRedirect();
  return (
    <Edit
      {...props}
      mutationMode="pessimistic"
      mutationOptions={{
        onSuccess: () => {
          notify("Credit transaction updated successfully!", {
            type: "success",
          });
          refresh();
          redirect(false);
        },
        onError: (error) => {
          notify(`Error: ${error.message}`, { type: "error" });
        },
      }}
    >
      {renderEditForm()}
    </Edit>
  );
}

const ListActions = () => {
  return (
    <TopToolbar>
      <FilterButton />
      <CreateButton />
      <DownloadExportButton2 />
    </TopToolbar>
  );
};

function ListView(props) {
  return (
    <List
      {...props}
      actions={<ListActions />}
      exporter={true}
      filters={[
        <SpeakerInput
          source="speaker_id"
          label="Speaker"
          key="speaker_id"
          alwaysOn
        />,
        <DateInput
          source="created_at.gte"
          label="From Date"
          key="created_at_gte"
        />,
        <DateInput
          source="created_at.lte"
          label="To Date"
          key="created_at_lte"
        />,
      ]}
      sort={{ field: "created_at", order: "DESC" }}
      title="Speaker Credit Transactions"
    >
      <Datagrid rowClick="edit" bulkActionButtons={false}>
        <TextField source="id" label="ID" />
        <ReferenceField
          label="Speaker"
          source="speaker_id"
          reference="speakers"
          linkType="edit"
        >
          <TextField source="name" />
        </ReferenceField>
        <NumberField source="credits" label="Credits" />
        <TextField source="source" label="Source" />
        <DateField source="created_at" label="Date" showTime />
      </Datagrid>
    </List>
  );
}

export const TaCreditTransactions = {
  category: "database",
  name: "ta-credit-transactions",
  options: { label: "TA Credit Transactions" },
  create: CreateView,
  edit: EditView,
  icon: DollarSign,
  list: ListView,
  recordRepresentation: (record) =>
    `${record.credits > 0 ? "+" : ""}${record.credits} credits`,
};
